
import { AppSidebar, AppSidebarToggle, type SidebarProject, type SidebarThread } from "../components/app-sidebar.js";
import { AppSidebarProvider } from "../components/app-sidebar-primitives.js";
import { AppSettingsDialog } from "../components/app-settings-dialog.js";
import { mountShellPage } from "../components/shell-page-controller.js";

interface ShellPageData {
  username: string;
  fullName: string;
  projects: SidebarProject[];
  threads: SidebarThread[];
}

declare function loadShellPage(page: string): ShellPageData;

const styles = css`
  :host { display: block; height: 100%; min-width: 0; width: 100%; }
  .page { display: flex; height: 100%; width: 100%; }
  nav { height: 100%; }
  main { display: flex; flex: 1; flex-direction: column; min-height: 0; min-width: 0; }
  app-file-browser, app-automations, app-archived-threads { flex: 1; min-height: 0; }
  .mobile-bar { display: none; }
  @media (max-width: 767px) {
    .mobile-bar { border-bottom: 1px solid var(--border); display: flex; padding: 8px 12px; }
  }
`;

export class ShellPageView extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof ShellPageView)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["page","data-page"];

  #page;
  #pageSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #attr0;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #serverPayload;
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get page() {
    return this.#page;
  }

  set page(value) {
    const raw = value;
    const next = raw == null ? raw : raw;
    if (Object.is(this.#page, next)) return;
    this.#page = next;
    this.#pageSet = true;
    this.#updateBindings('page');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-page')) this.page = this.dataset.page;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.page = props.page;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#pageSet) this.page = this.dataset.page;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => mountShellPage(this, this.#page))();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'page':
      case 'data-page':
        this.page = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const data = this.#server("data");
    return `<style>${this.#escText(styles)}</style><app-sidebar-provider><!--argon-text:0-->${""}<!--/argon-text:0--><div class="page"><nav><app-sidebar data-projects="${this.#attrEsc(data.projects)}" data-threads="${this.#attrEsc(data.threads)}" data-username="${this.#attrEsc(data.username)}" data-full-name="${this.#attrEsc(data.fullName)}" data-argon-bind="0"><!--argon-text:1-->${""}<!--/argon-text:1--></app-sidebar></nav><main><div class="mobile-bar"><app-sidebar-toggle><!--argon-text:2-->${""}<!--/argon-text:2--></app-sidebar-toggle></div><!--argon-text:3-->${(this.#page === "files") ? "<app-file-browser></app-file-browser>" : (this.#page === "automations") ? "<app-automations></app-automations>" : "<app-archived-threads></app-archived-threads>"}<!--/argon-text:3--></main><app-settings-dialog><!--argon-text:4-->${""}<!--/argon-text:4--></app-settings-dialog></div></app-sidebar-provider>`;
  }


  #server(name) {
    if (!this.#serverPayload) {
      const encoded = this.getAttribute('data-argon-server');
      if (!encoded) throw new Error('Argon server binding "' + name + '" needs SSR hydration data.');
      this.#serverPayload = JSON.parse(encoded);
    }
    if (!(name in this.#serverPayload)) throw new Error('Argon server binding "' + name + '" is missing from hydration data.');
    const value = this.#serverPayload[name];
    if (value && typeof value === 'object' && !Object.prototype.hasOwnProperty.call(value, 'refresh')) {
      Object.defineProperty(value, 'refresh', { enumerable: false, value: () => this.#refreshServer(name) });
    }
    return value;
  }

  async #refreshServer(name) {
    const response = await fetch('/__argon/shell-page-view/' + encodeURIComponent(name), { method: 'POST' });
    if (!response.ok) throw new Error('Argon server refresh failed: ' + response.status);
    this.#serverPayload[name] = await response.json();
    this.#updateBindings();
    return this.#server(name);
  }


  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const data = this.#server("data");
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'page') {
      const __argonNext = `${(this.#page === "files") ? "<app-file-browser></app-file-browser>" : (this.#page === "automations") ? "<app-automations></app-automations>" : "<app-archived-threads></app-archived-threads>"}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(data.projects)}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('data-projects', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(data.threads)}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('data-threads', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(data.username)}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr0.setAttribute('data-username', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(data.fullName)}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr0.setAttribute('data-full-name', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;

    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
